import { Info } from "@mui/icons-material";
import { FC, useState } from "react";
import avatar from "src/assets/images/header/avatar.svg";
import CallDetailsModal from "./CallDetailsModal";
import { formatDateDmy, formatTime } from "src/utils";

// Import CallLogs type from booking types
interface CallLogs {
	_id: string;
	sender: string;
	booking_id: string;
	message: string;
	message_type: string;
	message_read: boolean;
	call_status: boolean;
	chat_view_status: boolean;
	message_date: string;
	createdAt: string;
	updatedAt: string;
	__v: number;
	call_duration: number;
}

interface CallLogsModalProps {
	open: boolean;
	handleClose: () => void;
	callLogs: CallLogs[];
	customerName: string;
	vendorName: string;
	customerAvatar: string;
	vendorAvatar: string;
}

const CallLogsModal: FC<CallLogsModalProps> = ({
	open,
	handleClose,
	callLogs,
	customerName,
	vendorName,
	customerAvatar,
	vendorAvatar,
}) => {
	const [tab, setTab] = useState<"all" | "missed">("all");
	const [openCallDetails, setOpenCallDetails] = useState(false);
	const [selectedCall, setSelectedCall] = useState<any>(null);

	// Format call logs for display
	const formatCallLogs = (logs: CallLogs[]) => {
		return logs.map((log) => {
			try {
				const callDate = new Date(log.createdAt);
				const now = new Date();
				const diffTime = Math.abs(now.getTime() - callDate.getTime());
				const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));

				let timeDisplay = "";
				let dateDisplay = "";

				if (diffDays === 1) {
					dateDisplay = "Yesterday";
				} else if (diffDays === 2) {
					dateDisplay = "2 days ago";
				} else if (diffDays > 2) {
					dateDisplay = formatDateDmy(log.message_date || log.createdAt);
				} else {
					timeDisplay = formatTime(callDate);
				}

				// Determine call status based on call_status
				const status = log.call_status ? "received" : "missed";

				// Determine direction and participants based on sender
				// This logic might need adjustment based on your actual API data structure
				// For now, we'll assume the sender indicates who initiated the call
				const isCustomerSender =
					log.sender.toLowerCase().includes("customer") ||
					log.sender.toLowerCase().includes("user");

				const direction = isCustomerSender ? "outgoing" : "incoming";
				const from = isCustomerSender ? customerName : vendorName;
				const to = isCustomerSender ? vendorName : customerName;

				return {
					id: log._id,
					from,
					to,
					direction,
					status,
					time: timeDisplay,
					date: dateDisplay,
					avatars: [customerAvatar, vendorAvatar],
					originalData: log,
					callDuration: log.call_duration,
					messageDate: log.message_date || log.createdAt,
				};
			} catch (error) {
				// Return a fallback object if there's an error parsing the log
				return {
					id: log._id,
					from: customerName,
					to: vendorName,
					direction: "incoming",
					status: log.call_status ? "received" : "missed",
					time: "",
					date: "Unknown",
					avatars: [customerAvatar, vendorAvatar],
					originalData: log,

					callDuration: log.call_duration,
					messageDate: log.message_date || log.createdAt,
				};
			}
		});
	};

	const formattedLogs = formatCallLogs(callLogs);
	const filteredLogs =
		tab === "all"
			? formattedLogs
			: formattedLogs.filter((log) => log.status === "missed");

	if (!open) return null;

	return (
		<>
			<div
				className='fixed inset-0 z-40 bg-black/30 transition-opacity'
				onClick={handleClose}
			/>
			<div className='fixed top-0 right-0 z-50 h-full w-full max-w-[420px] bg-white shadow-2xl flex flex-col'>
				{/* Header */}
				<div className='flex items-center gap-3 px-6 py-4 border-b border-gray-100 relative'>
					<button
						className='mr-2 p-1 rounded-full hover:bg-gray-100'
						onClick={handleClose}
					>
						<svg
							width='24'
							height='24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							viewBox='0 0 24 24'
						>
							<path
								strokeLinecap='round'
								strokeLinejoin='round'
								d='M15 19l-7-7 7-7'
							/>
						</svg>
					</button>
					<div className='flex items-center gap-2 flex-1 justify-center'>
						<img
							src={customerAvatar}
							alt='customer'
							className='w-8 h-8 rounded-full border-2 border-white -mr-2 z-10'
						/>
						<img
							src={vendorAvatar}
							alt='vendor'
							className='w-8 h-8 rounded-full border-2 border-white z-0'
						/>
						<span className='ml-3 font-semibold text-gray-900'>
							{customerName} & {vendorName}'s Call Logs
						</span>
					</div>
					<button className='ml-auto p-1 rounded-full hover:bg-gray-100'>
						<svg
							width='24'
							height='24'
							fill='none'
							stroke='currentColor'
							strokeWidth='2'
							viewBox='0 0 24 24'
						>
							<circle cx='12' cy='6' r='1.5' />
							<circle cx='12' cy='12' r='1.5' />
							<circle cx='12' cy='18' r='1.5' />
						</svg>
					</button>
				</div>
				{/* Tabs */}
				<div className='flex gap-8 px-6 pt-4 pb-2 border-b border-gray-100'>
					<button
						className={`pb-2 font-semibold ${
							tab === "all"
								? "text-purple-600 border-b-2 border-purple-600"
								: "text-gray-400"
						}`}
						onClick={() => setTab("all")}
					>
						All ({formattedLogs.length})
					</button>
					<button
						className={`pb-2 font-semibold ${
							tab === "missed"
								? "text-purple-600 border-b-2 border-purple-600"
								: "text-gray-400"
						}`}
						onClick={() => setTab("missed")}
					>
						Missed (
						{formattedLogs.filter((log) => log.status === "missed").length})
					</button>
				</div>
				{/* Call Logs List */}
				<div className='flex-1 overflow-y-auto px-6 py-4 bg-white'>
					{filteredLogs.length === 0 ? (
						<div className='text-center py-8 text-gray-500'>
							No call logs found
						</div>
					) : (
						filteredLogs.map((log) => {
							return (
								<div
									key={log.id}
									className='flex items-center py-3 border-b border-gray-50'
								>
									<div className='flex items-center mr-3'>
										<span className='w-9 h-9 rounded-full flex items-center justify-center bg-gray-100 mr-2'>
											{log.status === "missed" ? (
												<img src='/svgs/phone3.svg' alt='' />
											) : (
												<img src='/svgs/phone2.svg' alt='' />
											)}
										</span>
										<div className='flex flex-col'>
											<span className='font-medium text-gray-900 text-sm'>
												{log.from} to {log.to}
											</span>
											<div className='flex items-center gap-2'>
												<div className='flex items-center'>
													<img
														src={log.avatars[0]}
														alt='a'
														className='w-7 h-7 rounded-full border-2 border-white -mr-2'
													/>
													<img
														src={log.avatars[1]}
														alt='b'
														className='w-7 h-7 rounded-full border-2 border-white'
													/>
												</div>
												<span
													className={`text-xs ${
														log.status === "missed"
															? "text-red-500"
															: "text-gray-400"
													}`}
												>
													{log.status === "missed" ? "Missed" : "Received"}
												</span>
											</div>
										</div>
									</div>
									<div className='flex items-center gap-2 ml-auto'>
										<span className='text-xs text-gray-400 mr-2'>
											{formatDateDmy(log?.originalData?.createdAt)}
										</span>
										<span
											className='w-6 h-6 flex items-center justify-center text-gray-400 cursor-pointer'
											onClick={() => {
												setSelectedCall(log);
												setOpenCallDetails(true);
											}}
										>
											<Info />
										</span>
									</div>
								</div>
							);
						})
					)}
				</div>
			</div>
			<CallDetailsModal
				open={openCallDetails}
				handleClose={() => setOpenCallDetails(false)}
				call={selectedCall}
			/>
		</>
	);
};

export default CallLogsModal;
